import { checkSessionSync, getTokenBack, logout } from "../utils/auth";
import { onError } from "@apollo/client/link/error";
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloLink,
  concat,
  split
} from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import store from "../store/store";
import { backendUrl } from "../utils/enviroments";

const INVALID_TOKEN_ERROR: string = "TOKEN DE AUTHORIZACION INVALIDO";
const DEBUG_VO =
  typeof window !== "undefined" &&
  (Boolean((window as any).__VO_DEBUG__) || window.localStorage?.getItem("__VO_DEBUG__") === "1");

const getWsGraphqlUrl = (): string | null => {
  const explicitWsUrl = process.env.REACT_APP_BACKEND_WS_URL || process.env.REACT_APP_BACKEND_SOCKET;
  if (explicitWsUrl) {
    return explicitWsUrl.endsWith("/graphql")
      ? explicitWsUrl
      : `${explicitWsUrl}/graphql`;
  }
  if (!backendUrl) return null;
  const normalized = backendUrl.replace(/\/+$/, "");
  const wsBase = normalized.startsWith("https://")
    ? normalized.replace(/^https:\/\//, "wss://")
    : normalized.startsWith("http://")
      ? normalized.replace(/^http:\/\//, "ws://")
      : normalized;
  return wsBase.endsWith("/graphql") ? wsBase : `${wsBase}/graphql`;
};

const isSubscriptionOperation = ({ query }: { query: any }): boolean => {
  const definition = getMainDefinition(query);
  return definition.kind === "OperationDefinition" && definition.operation === "subscription";
};

export const createApolloClient = () => {
  const httpLink = new HttpLink({
    uri: backendUrl + "/graphql",
  });

  const authMiddleware = new ApolloLink((operation, forward) => {
    store.dispatch({ type: "globalLoading", payload: true });
    if (
      operation.operationName !== "LOGIN" &&
      operation.operationName !== "RESTOREPASS"
    ) {
      operation.setContext({
        headers: {
          authorization: "Bearer " + getTokenBack(),
        },
      });
      checkSessionSync();
    }
    return forward(operation).map((data) => {
      store.dispatch({ type: "globalLoading", payload: false });
      return data;
    });
  });

  const logoutLink = onError((networkError: any) => {
    store.dispatch({ type: "globalLoading", payload: false });
    if (networkError) {
      if (
        networkError.response &&
        networkError.response.errors &&
        networkError.response.errors.some(
          (error: any) => error.message === INVALID_TOKEN_ERROR
        )
      ) {
        logout();
      }
    }
  });

  const httpChain = concat(authMiddleware, logoutLink.concat(httpLink));

  const wsGraphqlUrl = getWsGraphqlUrl();
  const hasBrowserSocket = typeof window !== "undefined" && typeof window.WebSocket !== "undefined";
  const wsLink = wsGraphqlUrl && hasBrowserSocket
    ? new GraphQLWsLink(
      createClient({
        url: wsGraphqlUrl,
        lazy: true,
        retryAttempts: Infinity,
        shouldRetry: () => true,
        connectionParams: () => ({
          authorization: "Bearer " + getTokenBack(),
        }),
        on: DEBUG_VO
          ? {
            connected: () => {
              // eslint-disable-next-line no-console
              console.log("[VO][ws][connected]", { url: wsGraphqlUrl });
            },
            closed: (event) => {
              // eslint-disable-next-line no-console
              console.warn("[VO][ws][closed]", { code: event.code, reason: event.reason });
            },
            error: (error) => {
              // eslint-disable-next-line no-console
              console.error("[VO][ws][error]", error);
            },
          }
          : undefined,
      })
    )
    : null;

  if (!wsLink && DEBUG_VO) {
    // eslint-disable-next-line no-console
    console.warn("[VO][ws][disabled]", { wsGraphqlUrl, hasBrowserSocket });
  }

  const link = wsLink
    ? split(isSubscriptionOperation, wsLink, httpChain)
    : httpChain;

  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
  });
};
